// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { formatUsDate, formatUsDateTime } from '../../api/format'
import { Loader2, ShieldCheck, ChevronRight, CheckCircle2, AlertTriangle, CircleDashed, Minus } from 'lucide-react'
import * as api from '../../api/client'
import { rowOpen, rowActions } from './rowOpen'
import { friendlyError } from '../../api/errors'
import { ColumnFilterButton, ColumnFilterChips, applyColumnFilters, filterToken, toDateKey, type ColumnFilters } from '../../components/common/ColumnFilter'
import SearchableSelect from '../../components/common/SearchableSelect'
import CareRxClinicalReviewDetail from './CareRxClinicalReviewDetail'
import LanguageBadge from '../../components/ui/LanguageBadge'
import TherapyRefTag from '../../components/ui/TherapyRefTag'
import { useClinicalCounts, useRefreshClinicalCounts } from '../../hooks/queries/carerxClinical'
import { StatTile } from '../../components/ui/Section'
import {
  CR_TILES, activeCrTile, archiveOf, clickCrTile, crListQuery, crTileCount, crViewRows, nextCrSort,
  type CrArchive, type CrQuick, type CrSort, type CrSortKey, type CrTileKey,
} from './clinicalReviewTiles'
import TruncatedText from '../../components/ui/TruncatedText'
import TruncationNotice from '../../components/ui/TruncationNotice'
import ReviewAssignee from './ReviewAssignee'
import { CrStatusSelect, CrDeterminationControl, STATUS_LABEL, DETERM_LABEL } from '../../components/carerx/ClinicalReviewDecision'

// D-CRX-45 Clinical Review — therapy-grain worklist. Replaces the patient-picker PCW home.
// Full column set (CR-D1), new status vocab (CR-D3), determination routing (CR-D4..D7),
// RPH assignment (CR-D8..D10/V3), priority override (CR-G2/8). Quick filters default Pending.

const PRIORITIES = ['urgent', 'high', 'normal', 'low']

const tag = (style: React.CSSProperties): React.CSSProperties => ({
  fontSize: 11, padding: '2px 9px', borderRadius: 100, fontWeight: 600, whiteSpace: 'nowrap',
  display: 'inline-block', ...style,
})

// D-CRX-64: does this record still need labs? The server sends `labs_flag`, derived from the same
// required-analytes / entered-results counts the approval gate enforces (H-5), so a green check here
// always means Approve will not stop for labs. Older API bundles omit the field — render nothing
// rather than guess a state we were not told.
const LABS_UI: Record<string, { icon: typeof CheckCircle2; color: string; bg: string; label: string; help: (r: api.ClinicalReviewRow) => string }> = {
  complete: { icon: CheckCircle2, color: '#0d6b52', bg: '#e1f4ec', label: 'Labs on file',
    help: r => `All ${r.required_analytes} required analyte(s) have results.` },
  partial: { icon: CircleDashed, color: '#93610f', bg: '#fff7e6', label: 'Partial',
    help: r => `${r.analytes_with_results} of ${r.required_analytes} required analytes have results — the rest are still outstanding.` },
  missing: { icon: AlertTriangle, color: '#b23a48', bg: '#fbecee', label: 'Needs labs',
    help: r => `${r.required_analytes} required analyte(s), no results entered. Approving is refused until results are entered or a pharmacist attests (audited).` },
  not_required: { icon: Minus, color: '#8496a5', bg: 'transparent', label: 'None required',
    help: () => 'This therapy has no required analytes.' },
}

function LabsFlag({ row }: { row: api.ClinicalReviewRow }) {
  const ui = row.labs_flag ? LABS_UI[row.labs_flag] : undefined
  if (!ui) return <span style={{ color: '#8496a5' }}>—</span>
  const Icon = ui.icon
  return (
    <span title={ui.help(row)} aria-label={`Labs: ${ui.label}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
               background: ui.bg, color: ui.color, borderRadius: 100, padding: ui.bg === 'transparent' ? 0 : '2px 9px',
               fontSize: 11, fontWeight: 600 }}>
      <Icon size={14} aria-hidden="true" />
      {ui.label}
      {row.labs_flag === 'partial' && <span style={{ fontWeight: 500 }}>({row.analytes_with_results}/{row.required_analytes})</span>}
    </span>
  )
}

export default function CareRxClinicalReview() {
  const [rows, setRows] = useState<api.ClinicalReviewRow[]>([])
  const [truncated, setTruncated] = useState(false)
  const [cap, setCap] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState<string | null>(null)
  const [quick, setQuick] = useState<CrQuick>('pending')
  // CareRx wave-1 #2: a dashboard tile that narrows the queue beyond a status (Unassigned, Needs labs).
  const [tile, setTile] = useState<CrTileKey | null>(null)
  // CareRx wave-1 #3: server-side sort on Next infusion / Deliver on (null = priority, then newest).
  const [sort, setSort] = useState<CrSort | null>(null)
  // ASP 2026-10-11 (#15): a decided review leaves the queue (approved ⇒ Active, denied ⇒ Cancelled).
  // The Approved / Denied chips show those outcomes, read-only, from their own list. Wave-1 #2: the
  // Conditional tile's set (it sits in Screening & Hold) is a read-only list of the same kind.
  const archiveView = archiveOf(quick)
  const decidedView = archiveView !== null
  const [archive, setArchive] = useState<Record<CrArchive, api.ClinicalReviewRow[] | null>>({ decided: null, conditional: null })
  const [archiveTruncated, setArchiveTruncated] = useState<Record<CrArchive, boolean>>({ decided: false, conditional: false })
  const archiveOpened = useRef(new Set<CrArchive>())
  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  const [openTherapy, setOpenTherapy] = useState<string | null>(null)

  // The sort and the two date funnels run in SQL (before the cap); the request changes only when they do.
  const listQuery = crListQuery(sort, colFilters)
  const listSig = api.clinicalReviewListQs(listQuery)
  const listQueryRef = useRef(listQuery)
  listQueryRef.current = listQuery
  // Only the LAST request of each list may land — a slower, older answer must not overwrite it.
  const seq = useRef({ queue: 0, decided: 0, conditional: 0 })
  const loadArchive = useCallback((v: CrArchive) => {
    const n = ++seq.current[v]
    api.listClinicalReview({ ...listQueryRef.current, view: v })
      .then(d => {
        if (n !== seq.current[v]) return
        setArchive(a => ({ ...a, [v]: d.items ?? [] })); setArchiveTruncated(t => ({ ...t, [v]: !!d.truncated }))
      })
      // A failed archive load must not leave the view spinning: show it empty under the error.
      .catch(e => { if (n === seq.current[v]) { setMsg(friendlyError(e)); setArchive(a => ({ ...a, [v]: a[v] ?? [] })) } })
  }, [])

  // ASP #23: every reload refreshes the stage counts — an action here may have moved a therapy on.
  const refreshCounts = useRefreshClinicalCounts()
  const clinicalCounts = useClinicalCounts()
  const load = useCallback(() => {
    const n = ++seq.current.queue
    setLoading(true)
    refreshCounts()
    api.listClinicalReview(listQueryRef.current)
      .then(d => { if (n === seq.current.queue) { setRows(d.items ?? []); setTruncated(!!d.truncated); setCap(d.cap ?? null) } })
      .catch(e => { if (n === seq.current.queue) setMsg(friendlyError(e)) })
      .finally(() => { if (n === seq.current.queue) setLoading(false) })
    // An archive is fetched only once it is opened (and refreshed with the queue after that), so the
    // working queue never pays for a list it is not showing.
    for (const v of archiveOpened.current) loadArchive(v)
  }, [refreshCounts, loadArchive, listSig])   // eslint-disable-line react-hooks/exhaustive-deps -- listSig stands for listQueryRef.current
  useEffect(() => { load() }, [load])

  // The rows the current view draws from — the queue, or one read-only list. Funnels list values
  // from it, so a read-only view never offers (or filters on) a queue-only value.
  const base = useMemo(() => archiveView === 'decided' ? (archive.decided ?? []).filter(x => x.determination === quick)
    : archiveView === 'conditional' ? (archive.conditional ?? []) : rows,
    [archiveView, archive, quick, rows])

  const filtered = useMemo(() => {
    const r = crViewRows(quick, tile, rows, archive)
    return applyColumnFilters(r, colFilters, (row, key) => {
      // D-UI-2: every key returns what its cell SHOWS (the label, not the code).
      switch (key) {
        case 'rx': return row.rx_number
        case 'patient': return row.patient_name
        case 'drug': return row.drug_name
        case 'status': return STATUS_LABEL[row.cr_status] ?? row.cr_status
        case 'priority': return row.priority ? row.priority.toUpperCase() : row.priority
        case 'determination': return DETERM_LABEL[row.determination] ?? row.determination
        case 'clinical_notes': return row.clinical_notes
        case 'last_review': return row.last_review_note
        // A FILTER value, never rendered: the day the From / To bounds compare against.
        case 'next_infusion': return toDateKey(row.next_infusion) ?? ''
        case 'deliver_on': return toDateKey(row.deliver_on) ?? ''
        case 'allergies': return row.allergies
        case 'therapy_type': return row.therapy_type
        case 'care_plan_type': return row.care_plan_type
        case 'rph': return row.rph_name
        case 'labs': return row.labs_flag ? LABS_UI[row.labs_flag]?.label ?? '' : ''
        default: return ''
      }
    })
  }, [quick, tile, rows, archive, colFilters])

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const r of rows) c[r.cr_status] = (c[r.cr_status] || 0) + 1
    for (const r of archive.decided ?? []) c[r.determination] = (c[r.determination] || 0) + 1
    if (archive.conditional) c['conditional'] = archive.conditional.length
    return c
  }, [rows, archive])

  // Picking a list. Switching between the queue and a read-only list clears the column filters:
  // their values come from the other list.
  const selectQuick = (q: CrQuick, nextTile: CrTileKey | null = null) => {
    const to = archiveOf(q)
    if (to !== archiveView) setColFilters({})
    if (to && !archiveOpened.current.has(to)) { archiveOpened.current.add(to); loadArchive(to) }
    setQuick(q); setTile(nextTile)
  }
  // Dashboard tiles (CareRx wave-1 #2): each one is the filter its number counts (clinicalReviewTiles.ts).
  // A tile shows exactly its set, so the header funnels it would otherwise intersect are cleared.
  const activeTile = activeCrTile(quick, tile)
  const onTile = (k: CrTileKey) => {
    const next = clickCrTile(activeTile, k)
    setColFilters({})
    selectQuick(next.quick, next.tile)
  }
  const readOnlyLabel = quick === 'approved' ? 'Approved' : quick === 'denied' ? 'Denied' : 'Conditional'

  // A successful write clears any earlier error — a stale banner read as "this save failed too".
  const saved = () => { setMsg(null); load() }
  const patch = async (fn: () => Promise<unknown>) => {
    try { await fn(); saved() } catch (e) { setMsg(friendlyError(e)) }
  }

  if (openTherapy) {
    // A decided review opens without the Status / Determination controls: it is a record now.
    return <CareRxClinicalReviewDetail therapyId={openTherapy} showDecision={!decidedView} readOnly={decidedView}
      backLabel={decidedView ? `Back to ${readOnlyLabel} reviews` : undefined}
      onBack={() => { setOpenTherapy(null); saved() }} />
  }

  const th: React.CSSProperties = { fontSize: 10.5, letterSpacing: '.03em', textTransform: 'uppercase', color: '#8496a5', fontWeight: 700, textAlign: 'left', padding: '8px 10px', borderBottom: '1.5px solid #dde5ec', whiteSpace: 'nowrap' }
  const td: React.CSSProperties = { padding: '9px 10px', borderBottom: '1px solid #eef2f6', fontSize: 12.5, verticalAlign: 'middle' }
  const sel: React.CSSProperties = { fontSize: 12, padding: '3px 6px', border: '1px solid #dde5ec', borderRadius: 6, background: '#fff' }
  // Wave-1 #3: a sortable header. The sort runs on the server; the funnel beside it is unchanged.
  const sortHead = (key: CrSortKey, label: string) => (
    <button type="button" onClick={() => setSort(s => nextCrSort(s, key))} title={`Sort by ${label}`}
      style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', cursor: 'pointer' }}>
      {label}{sort?.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </button>
  )
  const ariaSort = (key: CrSortKey) => sort?.key === key ? (sort.dir === 'asc' ? 'ascending' as const : 'descending' as const) : undefined

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ShieldCheck size={18} color="#185FA5" />
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 660 }}>Clinical Review</h2>
        <span style={{ fontSize: 12, color: '#8496a5' }}>Therapy-grain worklist · {filtered.length} shown</span>
      </div>

      {/* Dashboard — same card strip as Lab Tracker / Screening & Hold */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {CR_TILES.map(t => {
          const n = crTileCount(t, rows, clinicalCounts.data)
          const tone = n ? t.tone : undefined
          return <StatTile key={t.key} label={t.label} value={n ?? '—'}
            color={tone === 'bad' ? '#b23a48' : tone === 'warn' ? '#93610f' : '#1f2d3d'}
            active={activeTile === t.key} onClick={() => onTile(t.key)} />
        })}
      </div>

      {truncated && <TruncationNotice shown={cap ?? rows.length} what="therapies in review"
        detail="narrow with the filters below to see the rest; full pagination is coming" />}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {(['pending', 'draft', 'in_progress', 'pending_callback', 'cancelled', 'all', 'approved', 'denied', 'conditional'] as const).map(q => (
          <button key={q} aria-pressed={quick === q && !tile} onClick={() => selectQuick(q)} style={{
            fontSize: 11.5, padding: '4px 11px', borderRadius: 100, cursor: 'pointer',
            border: '1px solid ' + (quick === q && !tile ? '#185FA5' : '#dde5ec'),
            background: quick === q && !tile ? '#185FA5' : '#fff', color: quick === q && !tile ? '#fff' : '#41566a',
          }}>
            {q === 'all' ? 'All' : q === 'approved' ? 'Approved' : q === 'denied' ? 'Denied' : q === 'conditional' ? 'Conditional' : STATUS_LABEL[q]}{q !== 'all' && counts[q] ? ` (${counts[q]})` : ''}
          </button>
        ))}
      </div>

      <ColumnFilterChips filters={colFilters} labels={{ rx: 'RX #', patient: 'Patient', drug: 'Drug', status: 'Status', priority: 'Priority', determination: 'Determination', clinical_notes: 'Clinical notes', last_review: 'Last review', next_infusion: 'Next infusion', deliver_on: 'Deliver on', allergies: 'Allergies', therapy_type: 'Therapy type', care_plan_type: 'Care plan type', rph: 'Pharmacist', labs: 'Labs' }}
        onClear={key => setColFilters(f => { const n = { ...f }; delete n[key]; return n })}
        onClearAll={() => setColFilters({})} />

      {msg && <div style={{ fontSize: 12.5, color: '#b23a48', background: '#fbecee', borderRadius: 8, padding: '8px 11px', margin: '8px 0' }}>{msg}</div>}
      {archiveView && <div style={{ fontSize: 12, color: '#41566a', margin: '4px 0 8px' }}>
        {quick === 'approved' ? 'Approved reviews — the therapy is Active.' : quick === 'denied' ? 'Denied reviews — the therapy was cancelled.'
          : 'Conditional reviews — each opened a Screening & Hold item; it returns to the queue when the hold resolves.'} Read-only here except the pharmacist; open one to see the full review.
        {archiveTruncated[archiveView] && ' Showing the first 1,000.'}</div>}

      {loading || (archiveView && archive[archiveView] === null) ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#8496a5' }}><Loader2 className="spin" size={20} /> Loading…</div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto', border: '1px solid #dde5ec', borderRadius: 10 }}>
          <table data-funnels="own" style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1390 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <tr>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>RX # <ColumnFilterButton label="RX #" getValues={() => [...new Set(base.map(r => filterToken(r.rx_number)).filter(Boolean))]} selected={colFilters.rx ?? new Set()} onChange={next => setColFilters(f => ({ ...f, rx: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Patient <ColumnFilterButton label="Patient" getValues={() => [...new Set(base.map(r => filterToken(r.patient_name)).filter(Boolean))]} selected={colFilters.patient ?? new Set()} onChange={next => setColFilters(f => ({ ...f, patient: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Drug <ColumnFilterButton label="Drug" getValues={() => [...new Set(base.map(r => filterToken(r.drug_name)).filter(Boolean))]} selected={colFilters.drug ?? new Set()} onChange={next => setColFilters(f => ({ ...f, drug: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Status <ColumnFilterButton label="Status" getValues={() => [...new Set(base.map(r => filterToken(STATUS_LABEL[r.cr_status] ?? r.cr_status)).filter(Boolean))]} selected={colFilters.status ?? new Set()} onChange={next => setColFilters(f => ({ ...f, status: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Determination <ColumnFilterButton label="Determination" getValues={() => [...new Set(base.map(r => filterToken(DETERM_LABEL[r.determination] ?? r.determination)).filter(Boolean))]} selected={colFilters.determination ?? new Set()} onChange={next => setColFilters(f => ({ ...f, determination: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Labs <ColumnFilterButton label="Labs" getValues={() => [...new Set(base.map(r => filterToken(r.labs_flag ? LABS_UI[r.labs_flag]?.label : '')).filter(Boolean))]} selected={colFilters.labs ?? new Set()} onChange={next => setColFilters(f => ({ ...f, labs: next }))} /></span></th>
                {/* ASP #25/#33: the enrollment's clinical notes (what the enrollment form saves) and the
                    latest review note. The old "Intake notes" column showed cr_intake_notes, which only
                    collects screening-override justifications — those are on the detail page now. */}
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Clinical notes <ColumnFilterButton label="Clinical notes" isText selected={colFilters.clinical_notes ?? new Set()} onChange={next => setColFilters(f => ({ ...f, clinical_notes: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Last review <ColumnFilterButton label="Last review" isText selected={colFilters.last_review ?? new Set()} onChange={next => setColFilters(f => ({ ...f, last_review: next }))} /></span></th>
                <th style={th} aria-sort={ariaSort('next_infusion')}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{sortHead('next_infusion', 'Next infusion')} <ColumnFilterButton label="Next infusion" isDate selected={colFilters.next_infusion ?? new Set()} onChange={next => setColFilters(f => ({ ...f, next_infusion: next }))} /></span></th>
                {/* ASP 2026-10-11 (#8): the enrollment's Deliver On date. */}
                <th style={th} aria-sort={ariaSort('deliver_on')}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{sortHead('deliver_on', 'Deliver on')} <ColumnFilterButton label="Deliver on" isDate selected={colFilters.deliver_on ?? new Set()} onChange={next => setColFilters(f => ({ ...f, deliver_on: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Pharmacist <ColumnFilterButton label="Pharmacist" getValues={() => [...new Set(base.map(r => filterToken(r.rph_name)).filter(Boolean))]} selected={colFilters.rph ?? new Set()} onChange={next => setColFilters(f => ({ ...f, rph: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Priority <ColumnFilterButton label="Priority" getValues={() => [...new Set(base.map(r => filterToken(r.priority ? r.priority.toUpperCase() : r.priority)).filter(Boolean))]} selected={colFilters.priority ?? new Set()} onChange={next => setColFilters(f => ({ ...f, priority: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Allergies <ColumnFilterButton label="Allergies" isText selected={colFilters.allergies ?? new Set()} onChange={next => setColFilters(f => ({ ...f, allergies: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Therapy type <ColumnFilterButton label="Therapy type" getValues={() => [...new Set(base.map(r => filterToken(r.therapy_type)).filter(Boolean))]} selected={colFilters.therapy_type ?? new Set()} onChange={next => setColFilters(f => ({ ...f, therapy_type: next }))} /></span></th>
                <th style={th}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Care plan type <ColumnFilterButton label="Care plan type" getValues={() => [...new Set(base.map(r => filterToken(r.care_plan_type)).filter(Boolean))]} selected={colFilters.care_plan_type ?? new Set()} onChange={next => setColFilters(f => ({ ...f, care_plan_type: next }))} /></span></th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.therapy_id} {...rowOpen(() => setOpenTherapy(r.therapy_id))}>
                  <td style={{ ...td, fontFamily: 'ui-monospace', fontWeight: 700 }}>{r.rx_number || '—'}</td>
                  <td style={td}>{r.patient_name || '—'}<LanguageBadge language={r.preferred_language} /><div><TherapyRefTag therapyId={r.therapy_id} /></div></td>
                  <td style={td}>{r.drug_name || '—'}</td>
                  {/* Owner UI surf 2026-08-13: shared-kit selects (E-rules) — the row's three
                      dropdowns were the last native <select>s on the tab. */}
                  {decidedView ? (<>
                    <td style={td}>{STATUS_LABEL[r.cr_status] ?? r.cr_status}</td>
                    <td style={td}>{DETERM_LABEL[r.determination] ?? r.determination}</td>
                  </>) : (<>
                    <td style={td} {...rowActions}>
                      <CrStatusSelect therapyId={r.therapy_id} value={r.cr_status} onSaved={saved} onError={setMsg} />
                    </td>
                    <td style={td} {...rowActions}>
                      <CrDeterminationControl therapyId={r.therapy_id} value={r.determination} onSaved={saved} onError={setMsg} />
                    </td>
                  </>)}
                  {/* No "also on hold" badge any more: a therapy with an open hold now leaves this
                      list entirely (D-CRX-68), so the two queues can no longer disagree. */}
                  <td style={td}><LabsFlag row={r} /></td>
                  <td style={{ ...td, maxWidth: 200 }}><TruncatedText text={r.clinical_notes} label="Clinical notes" /></td>
                  <td style={{ ...td, maxWidth: 200 }}>
                    <TruncatedText text={r.last_review_note} label="Last review"
                      heading={r.last_review_note ? `${r.last_review_by || 'Reviewer'} · ${formatUsDateTime(r.last_review_at)}` : null} />
                  </td>
                  <td style={td}>{r.next_infusion ? formatUsDate(r.next_infusion) : '—'}</td>
                  <td style={td}>{r.deliver_on ? formatUsDate(r.deliver_on) : '—'}</td>
                  {decidedView ? (<>
                    {/* ASP #27/#28: the pharmacist stays reassignable after the decision (keepStatus: the
                        decided status is never touched). */}
                    <td style={td} {...rowActions}>
                      <ReviewAssignee therapyId={r.therapy_id} rphId={r.assigned_rph_id} rphName={r.rph_name} assignedAt={r.assigned_at} keepStatus context="clinical_review"
                        onSelf={() => patch(() => api.assignClinicalReview(r.therapy_id, { self: true }))}
                        onChanged={m => { setMsg(m); load() }} />
                    </td>
                    <td style={td}>{r.priority ? r.priority.toUpperCase() : '—'}</td>
                  </>) : (<>
                    <td style={td} {...rowActions}>
                      <ReviewAssignee therapyId={r.therapy_id} rphId={r.assigned_rph_id} rphName={r.rph_name} assignedAt={r.assigned_at}
                        onSelf={() => patch(() => api.assignClinicalReview(r.therapy_id, { self: true }))}
                        onChanged={m => { setMsg(m); load() }} />
                    </td>
                    <td style={td} {...rowActions}>
                      <div style={{ minWidth: 110 }}><SearchableSelect value={r.priority} ariaLabel="Priority"
                        onChange={(v: string) => patch(() => api.setClinicalReviewPriority(r.therapy_id, v))}
                        options={PRIORITIES.map(p => ({ value: p, label: p.toUpperCase() }))} /></div>
                    </td>
                  </>)}
                  <td style={{ ...td, color: '#8496a5' }}>{r.allergies || '—'}</td>
                  <td style={td}>{r.therapy_type || '—'}</td>
                  <td style={td}>{r.care_plan_type ? <span style={tag(r.care_plan_type === 'renewed' ? { background: '#e1f4ec', color: '#0d6b52' } : { background: '#eaf2fb', color: '#185FA5' })}>{r.care_plan_type}</span> : '—'}</td>
                  <td style={td}>
                    <button onClick={() => setOpenTherapy(r.therapy_id)} title="Open review"
                      style={{ ...sel, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2 }}>Open <ChevronRight size={13} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={16} style={{ ...td, textAlign: 'center', color: '#8496a5', padding: 24 }}>No therapies in this view.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize: 11, color: '#41566a', marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span>Approved → Nursing &amp; Staffing (module TBD) · Conditional → Screening &amp; Hold · Denied → Cancelled + reason</span>
      </div>
    </div>
  )
}
